import React, { useEffect, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { useStripe, useElements } from "@stripe/react-stripe-js";

import Config from "../../../Config";
import "./walletTopup.css";
import { walletReturnUrl } from "../../../../utils/walletOrder";

/**
 * Pop-up 04 — recharger le portefeuille via Bancontact, depuis le checkout.
 *
 * Remplace les étapes 2 et 3 de WoshPayement (montant, puis nom et
 * paiement). L'échange avec Stripe est repris à l'identique de l'étape 3 :
 * seule l'interface change. Au retour de la banque, /wallet-success recharge
 * le solde et renvoie vers le checkout ; la commande n'est pas passée
 * automatiquement, l'utilisateur la confirme en revenant.
 */

export const PRESETS = [20, 50, 100];

/** Lit un montant saisi à la belge (« 9,58 ») ou à l'anglaise (« 9.58 »). */
export const parseAmount = (value) => {
  const parsed = parseFloat(String(value ?? "").replace(",", "."));

  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Ce qu'il manque pour couvrir la commande, arrondi au centime **supérieur** :
 * arrondir au plus proche pourrait laisser le portefeuille un centime court.
 */
export const shortfall = (total, balance) => {
  const missing = parseAmount(total) - parseAmount(balance);

  return missing > 0 ? Math.ceil(Math.round(missing * 10000) / 100) / 100 : 0;
};

export const formatEuro = (value) =>
  `€${(Number(value) || 0).toFixed(2).replace(".", ",")}`;

/** Tant qu'une de ces raisons existe, le bouton de recharge reste inactif. */
export const blockers = ({ amount, needed, name }) => {
  const reasons = [];

  if (!(amount > 0)) {
    reasons.push("amount");
  }

  if (amount < needed) {
    reasons.push("below-minimum");
  }

  /* Stripe exige le nom du titulaire pour un paiement Bancontact. */
  if (!String(name || "").trim()) {
    reasons.push("name");
  }

  return reasons;
};

const Icon = ({ children }) => (
  <svg viewBox="0 0 512 512" aria-hidden="true">
    {children}
  </svg>
);

const WalletTopup = ({ open, onClose, total, balance, authUser }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const needed = shortfall(total, balance);
  const [input, setInput] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);

  /* À chaque ouverture, on repart du minimum nécessaire. */
  useEffect(() => {
    if (open) {
      setInput(needed > 0 ? needed.toFixed(2).replace(".", ",") : "");
      setFailure(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, loading, onClose]);

  if (!open) {
    return null;
  }

  const amount = parseAmount(input);
  const tooLow = amount > 0 && amount < needed;
  const blocked = blockers({ amount, needed, name }).length > 0;

  const pay = async () => {
    setFailure(null);
    setLoading(true);

    /* /wallet-success renvoie ici une fois la banque passée. */
    localStorage.setItem("redirect", window.location.pathname);

    let secret;

    try {
      const response = await Axios({
        url: `${Config.apiURL}/customer/secret`,
        method: "GET",
        headers: {
          uid: authUser?.user_uid,
          client: authUser?.user_client,
          "access-token": authUser?.user_access_token,
        },
        params: {
          amount,
          laundry_place_id: JSON.parse(localStorage.getItem("laundry_id")),
        },
      });

      secret = response?.data?.client_secret;
    } catch (err) {
      setLoading(false);
      setFailure(
        err?.response?.data?.errors?.[0] ||
          t("Server is not responding at the moment, please try later")
      );
      return;
    }

    if (!stripe || !elements || !secret) {
      setLoading(false);
      setFailure(t("Server is not responding at the moment, please try later"));
      return;
    }

    const { error } = await stripe.confirmBancontactPayment(secret, {
      payment_method: {
        billing_details: { name: name.trim() },
      },
      return_url: walletReturnUrl(),
    });

    /* Sans erreur, Stripe a déjà redirigé vers la banque. */
    if (error) {
      setFailure(error.message);
      setLoading(false);
    }
  };

  return (
    <div
      className="wosh-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wosh-topup-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div className="wosh-topup">
        <div className="wosh-topup__head">
          <h2 className="wosh-topup__title" id="wosh-topup-title">
            {t("Top up your wallet")}
          </h2>
          <button
            type="button"
            className="wosh-topup__close"
            aria-label={t("Close")}
            disabled={loading}
            onClick={onClose}
          >
            <Icon>
              <line x1="368" y1="368" x2="144" y2="144" />
              <line x1="368" y1="144" x2="144" y2="368" />
            </Icon>
          </button>
        </div>

        {needed > 0 && (
          <div className="wosh-topup__due">
            <div className="wosh-topup__due-head">
              <Icon>
                <path d="M248,64C146.39,64,64,146.39,64,248s82.39,184,184,184,184-82.39,184-184S349.61,64,248,64Z" />
                <polyline points="220 220 252 220 252 336" />
                <line x1="208" y1="340" x2="296" y2="340" />
                <circle className="dot" cx="248" cy="156" r="22" />
              </Icon>
              {t("Your wallet needs a top-up")}
            </div>
            <div className="wosh-topup__due-row">
              <span>{t("Order total")}</span>
              <strong>{formatEuro(parseAmount(total))}</strong>
            </div>
            <div className="wosh-topup__due-row wosh-topup__due-row--last">
              <span>{t("Wallet balance")}</span>
              <strong>{formatEuro(parseAmount(balance))}</strong>
            </div>
            <div className="wosh-topup__due-total">
              <span>{t("To add")}</span>
              <span>{formatEuro(needed)}</span>
            </div>
            <div className="wosh-topup__due-note">
              {t("Top up at least")} {formatEuro(needed)}{" "}
              {t("to cover your order. You will then come back to your order to confirm it.")}
            </div>
          </div>
        )}

        <div className="wosh-topup__eyebrow">{t("Choose an amount")}</div>
        <div className="wosh-topup__presets">
          {PRESETS.map((preset) => (
            <button
              type="button"
              key={preset}
              className={`wosh-topup__preset${
                amount === preset ? " wosh-topup__preset--on" : ""
              }`}
              disabled={preset < needed || loading}
              onClick={() => setInput(String(preset))}
            >
              €{preset}
            </button>
          ))}
        </div>

        <label className="wosh-topup__label" htmlFor="wosh-topup-amount">
          {t("Or enter an amount")}
        </label>
        <div
          className={`wosh-topup__field${
            tooLow ? " wosh-topup__field--invalid" : ""
          }`}
        >
          <span>€</span>
          <input
            id="wosh-topup-amount"
            inputMode="decimal"
            value={input}
            disabled={loading}
            onChange={(event) =>
              setInput(event.target.value.replace(/[^0-9.,]/g, ""))
            }
          />
        </div>
        {tooLow && (
          <div className="wosh-topup__error">
            {t("The minimum amount is")} {formatEuro(needed)}.
          </div>
        )}

        {/* Absent du design, mais Stripe le rend obligatoire pour Bancontact. */}
        <label
          className="wosh-topup__label wosh-topup__gap"
          htmlFor="wosh-topup-name"
        >
          {t("Account holder name")}
        </label>
        <div className="wosh-topup__field wosh-topup__field--text">
          <input
            id="wosh-topup-name"
            autoComplete="name"
            placeholder={t("First and last name")}
            value={name}
            disabled={loading}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="wosh-topup__sum">
          <span>{t("You will add")}</span>
          <span>{formatEuro(amount)}</span>
        </div>

        <div className="wosh-topup__secure">
          <Icon>
            <path d="M336,208V113a80,80,0,0,0-160,0v95" />
            <rect x="96" y="208" width="320" height="272" rx="48" ry="48" />
          </Icon>
          <span>
            {t(
              "Payment is processed securely through Bancontact and Stripe. You will be redirected to your banking app to authorise it."
            )}
          </span>
        </div>

        {failure && <p className="wosh-topup__failure">{failure}</p>}

        <button
          type="button"
          className="wosh-btn"
          disabled={blocked || loading}
          onClick={pay}
        >
          {loading
            ? t("Processing")
            : `${t("Top up")} ${formatEuro(amount)} ${t("via Bancontact")}`}
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(WalletTopup);
